import React, { useRef, useImperativeHandle, forwardRef } from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import UploadImageButton from "components/common/UploadImageButton/UploadButton";
import { Field, FieldRef } from "../Field/Field";
import { StaffState } from "types/staff-edit.types";
import { CustomerRules, onlyNumberRule } from "utils/formRules";
import { ImagePayload } from "types/image.type";

interface InsuranceSectionProps {
  data: StaffState["insuranceInfo"];
  onUpdate: (path: string, value: string | ImagePayload[]) => void;
  handleUpload?: (file: File, path: string) => Promise<void>;
  isUploading?: boolean;
}

// Định nghĩa interface cho Ref
export interface InsuranceSectionRef {
  validateAll: () => Promise<boolean>;
}

export const InsuranceSection = forwardRef<
  InsuranceSectionRef,
  InsuranceSectionProps
>(({ data, onUpdate }, ref) => {
  // Tạo Ref cho trường BHXH
  const socialInsuranceRef = useRef<FieldRef>(null);

  // Công khai hàm validateAll
  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const results = await Promise.all([
        socialInsuranceRef.current?.validate(),
      ]);
      return results.every((r) => r === true);
    },
  }));

  return (
    <FourColumnRowSection
      leftTitle="Thông tin bảo hiểm"
      rightTitle=""
      rows={[
        {
          leftLabel: "Mã số BHXH",
          leftValue: (
            <Field
              ref={socialInsuranceRef} // Gán ref vào đây
              path="insuranceInfo.socialInsuranceNumber"
              value={data.socialInsuranceNumber}
              onUpdate={onUpdate}
              placeholder="Nhập mã số BHXH"
              rules={[...CustomerRules, onlyNumberRule()]}
            />
          ),
        },
        {
          leftLabel: "Ảnh",
          leftValue: (
            <div className="flex items-center gap-2">
              <UploadImageButton
                label="Tải lên ảnh BHXH"
                width={80}
                height={40}
                maxCount={2}
                isDisabled={false}
                imagesData={data.insuranceImages || []}
                setImagesData={(action) => {
                  const currentImages: ImagePayload[] = data.insuranceImages || [];
                  
                  const updatedImages: ImagePayload[] = typeof action === 'function' 
                    ? action(currentImages) 
                    : action;

                  onUpdate("insuranceInfo.insuranceImages", updatedImages);
                }}
              />
              {/* Lọc các chuỗi rỗng để đếm số lượng ảnh thực tế */}
              {data.insuranceImages && data.insuranceImages.length > 0 && (
                <span className="text-xs text-green-600 font-medium">
                  ✓ Đã chọn {data.insuranceImages.length} tệp
                </span>
              )}
            </div>
          ),
        },
      ]}
    />
  );
});

InsuranceSection.displayName = "InsuranceSection";
